import React from 'react';
import { Box, Modal, Typography, HorizontalScroll } from '@drone-js/core';
import { connect } from 'react-redux';
import { Loader } from 'component/common/Loader/Loader';
import ActivityCarousel from 'component/page/detail/DetailCard/ActivityCarousel/ActivityCarousel';
import MosaicComponent from 'component/page/detail/DetailCard/ActivityCarousel/MosaicComponent';
import { mapDispatchToProps, mapStateToProps, Props } from 'component/page/detail/DetailCard/ActivityCarousel/MosaicGallery.selectors';
import { body } from 'core-framework/window/document';
import Portal from 'component/common/Portal/Portal';
import styled from 'styled-components';
import { StringFormatter } from '@agoda/react-handyman';
import { ActivityImage } from 'component/common/ActivityImage/ActivityImage';
import { CmsValue } from 'core/type/BootstrapConfig.types';
import { Image as ImageType } from 'gql/autogen/Image';
import { MosaicGalleryVariant } from 'component/page/detail/DetailPageModularize/detailPageModularize.types';
import MainImage from 'component/page/detail/DetailCard/ActivityCarousel/MainImage';
import ScreenSize from 'component/common/Experiment/ScreenSize';

const MosaicModal = styled(Modal)`
    padding-left: 0 !important;
    padding-right: 0 !important;
`;

interface GalleryProps {
    index: number;
    cms: CmsValue;
    images: ImageType[];
}

const Gallery: React.FC<GalleryProps> = (props) => {
    const { cms, images, index } = props;
    const [activeIndex, setActiveIndex] = React.useState(0);
    const componentRef = React.useRef<HTMLDivElement>(null);
    const [width, setWidth] = React.useState('');

    React.useEffect(() => {
        if (componentRef.current) {
            setWidth(`${componentRef.current.getBoundingClientRect().width}px`);
        }
    }, []);

    React.useEffect(() => {
        setActiveIndex(index);
    }, [index]);

    return (
        <HorizontalScroll
            columnGap="2"
            nextLabel={cms[130706]}
            previousLabel={cms[130765]}
            behavior="item"
            scrollSnapAlign="center"
            onActiveChange={setActiveIndex}
            active={activeIndex}
            data-testid="horizontalscroll-image-gallery"
            ref={componentRef}
        >
            {images.map((item, i) => (
                <div key={i}>
                    <Box width={width} height="50vh">
                        <ActivityImage src={item.url || ''} width="100%" height="100%" objectFit="contain" alt={StringFormatter.format(cms[321481], i + 1, props.images.length)} />
                    </Box>
                    <Box px="16">
                        <Typography variant="caption-default-strong">{item.description || ''}</Typography>
                    </Box>
                </div>
            ))}
        </HorizontalScroll>
    );
};

Gallery.displayName = 'Gallery';

const MosaicGallery: React.FC<Props> = React.memo((props): React.JSX.Element => {
    const [modalState, setModalState] = React.useState({ visible: false, index: 0 });
    const gallerySize = props.screenSize.desktop ? 3 : 5;
    const MAINIMAGEHEIGHT = '268px';
    const imagesLength = props.images.length;
    const shouldShowTextOverlay = props.images.length > gallerySize;
    const isLessThanGallerySize = imagesLength < gallerySize;
    const mosaicImages = props.images.filter((image) => image.url && image.url.trim()).slice(0, Math.min(props.images.length, gallerySize));

    if (!props.images || imagesLength === 0) {
        return <Loader element="detail-image-carousel" />;
    }

    if (props.variant === MosaicGalleryVariant.SingleImage) {
        return (
            <Box data-testid="image-carousel" position="relative" width="100%" height={MAINIMAGEHEIGHT} p="0" borderRadius="16" overflow="hidden">
                <MainImage image={mosaicImages[0]} imageIndex={1} totalImages={1} isMosaicEnabled={true} height={MAINIMAGEHEIGHT} />
            </Box>
        );
    }

    const onImageClick = (e: React.MouseEvent, index: number, isViewAllImages: boolean) => {
        props.onImageClick(e, index, isViewAllImages);
        setModalState({ visible: true, index });
    };

    const onModalClose = () => {
        setModalState({ visible: false, index: 0 });
    };

    return (
        <>
            <Box data-testid="activities-mosaic" m="0">
                <ScreenSize.NonDesktop>
                    <ActivityCarousel onImageClick={onImageClick} />
                </ScreenSize.NonDesktop>
                <ScreenSize.Desktop>
                    <MosaicComponent
                        imagesLength={imagesLength}
                        mosaicImages={mosaicImages}
                        onImageClick={onImageClick}
                        shouldShowTextOverlay={shouldShowTextOverlay}
                        isLessThanGallerySize={isLessThanGallerySize}
                    />
                </ScreenSize.Desktop>
            </Box>
            <Portal container={body}>
                <MosaicModal
                    data-testid="activities-mosaic-modal-gallery"
                    size={props.screenSize.desktop ? 'l' : 'm'}
                    visible={modalState.visible}
                    onClose={onModalClose}
                    zIndex={1060}
                >
                    <Gallery index={modalState.index || 0} cms={props.cms} images={props.images} />
                </MosaicModal>
            </Portal>
        </>
    );
});

MosaicGallery.displayName = 'MosaicGallery';
export default connect(mapStateToProps, mapDispatchToProps)(MosaicGallery);
